import { useEffect } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useLibraries, useWorkspace } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { Spinner, EmptyState } from '@/components/Misc';
import { ScrollContainer } from '@/features/grid/ScrollContainer';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { CommandPalette } from './CommandPalette';
import { ShortcutsDialog } from './ShortcutsDialog';
import { BottomDock, DockProvider } from './BottomDock';
import { uploadManager } from '@/features/upload/UploadManager';
import { RealtimeProvider } from '@/lib/ws/RealtimeProvider';
import { useRoom } from '@/lib/ws/useRoom';
import { workspaceRoom } from '@/lib/shared';
import { isApiError } from '@/lib/api/errors';

/** §1.1/1.2 — resolves the workspace slug, sets the session store, renders sidebar + topbar + scroll container. */
export function WorkspaceLayout() {
  const { ws: slug } = useParams();
  const me = useSessionStore((s) => s.me);
  const setWorkspace = useSessionStore((s) => s.setWorkspace);
  // the API addresses workspaces by UUID only; the slug in the URL is resolved through the memberships
  // that /auth/me already returned (a user can only open workspaces they belong to)
  const membership = me?.memberships.find((m) => m.workspace.slug === slug) ?? null;
  const wsQ = useWorkspace(membership?.workspace.id);
  const ws = wsQ.data ?? null;
  useEffect(() => {
    if (ws) setWorkspace(ws, membership);
  }, [ws, membership, setWorkspace]);
  useEffect(() => {
    if (ws) void uploadManager.resumeFromStorage(ws.id);
  }, [ws]);
  const current = useSessionStore((s) => s.workspace);

  if (me && !membership)
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState title="Workspace not found" body="You are not a member of a workspace with this URL." />
      </div>
    );
  if (wsQ.isPending)
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size={20} />
      </div>
    );
  if (wsQ.isError || !ws)
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState title={isApiError(wsQ.error) && wsQ.error.status === 404 ? 'Workspace not found' : 'Could not load workspace'} body={isApiError(wsQ.error) ? wsQ.error.message : undefined} />
      </div>
    );
  if (!membership)
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState title="You are not a member of this workspace" />
      </div>
    );
  if (current?.id !== ws.id) return null;
  return (
    <RealtimeProvider>
      <DockProvider>
      <WorkspaceRoom wsId={ws.id} />
      <div className="flex h-full min-h-0 bg-bg">
        <Sidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar />
          <main className="relative min-h-0 flex-1 overflow-hidden">
            <ScrollContainer id="main-scroll">
              <Outlet />
            </ScrollContainer>
          </main>
        </div>
      </div>
      <BottomDock />
      <CommandPalette />
      <ShortcutsDialog />
      </DockProvider>
    </RealtimeProvider>
  );
}

function WorkspaceRoom({ wsId }: { wsId: string }) {
  useRoom(workspaceRoom(wsId));
  return null;
}

/** Index route of a workspace → its default library. */
export function WorkspaceIndex() {
  const ws = useSessionStore((s) => s.workspace);
  const libs = useLibraries(ws?.id);
  if (!ws) return null;
  const target = ws.default_library_id ?? libs.data?.[0]?.id;
  if (!target) return libs.isPending ? null : <EmptyState title="No libraries yet" />;
  return <Navigate to={`l/${target}`} replace />;
}
